import { callMsGraph } from '../graph';
import { checkMsGraphGroupMembership } from '../graphGroup';
import { instance } from '../index';
import { loginRequest } from '../authConfig';

// Cache สำหรับเก็บผลการตรวจสอบสิทธิ์ชั่วคราว
const permissionCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 นาที

// ฟังก์ชันล้าง cache (เรียกเมื่อ logout)
export function clearPermissionCache() {
  permissionCache.clear();
}

export async function getUserUniformGroup() {
  // Emergency timeout wrapper - ป้องกันการหมุนค้างทั้งหมด
  return new Promise((resolve) => {
  const emergencyTimeoutMs = Number(process.env.REACT_APP_AUTH_EMERGENCY_TIMEOUT_MS || 10000);
  const emergencyTimeout = setTimeout(() => {
      resolve(false);
  }, emergencyTimeoutMs); // emergency timeout
    
    _getUserUniformGroupInternal()
      .then(result => {
        clearTimeout(emergencyTimeout);
        resolve(result);
      })
      .catch(error => {
        clearTimeout(emergencyTimeout);
        resolve(false); // ส่ง false แทน reject
      });
  });
}

async function _getUserUniformGroupInternal() {
  try {
   // console.log('🔐 getUserUniformGroup: Function called');
    
    // ตรวจสอบ cache ก่อน (เพื่อประสิทธิภาพ)
    const cacheKey = 'uniform_permission';
    const cached = permissionCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.hasPermission;
    }

    // console.log('🔐 Checking permissions from Microsoft Graph API...');
    // console.log('🔐 Environment SECGROUPS_UNIFORM:', process.env.REACT_APP_SECGROUPS_UNIFORM);

    // อ่านกลุ่มจาก sessionStorage ก่อน (fast-path) เพื่อลดการเรียก Graph ใน UAT/PROD ที่ช้าหรือโดนบล็อก
    try {
      const cachedGroupsStr = sessionStorage.getItem('usergroup');
      if (cachedGroupsStr) {
        const cachedGroups = JSON.parse(cachedGroupsStr);
        const requiredGroups = JSON.parse(process.env.REACT_APP_SECGROUPS_UNIFORM || '[]');
        if (Array.isArray(cachedGroups) && cachedGroups.some((g) => requiredGroups.includes(g))) {
          //console.log('🔐 Fast-path: Matched required group from sessionStorage, skipping Graph');
          // เก็บผลลัพธ์ใน cache
          permissionCache.set('uniform_permission', {
            hasPermission: true,
            timestamp: Date.now(),
          });
          return true;
        }
      }
    } catch (e) {
      //console.warn('🔐 sessionStorage fast-path skipped due to parse error:', e);
    }
    
    // ตรวจสอบว่า MSAL instance พร้อมใช้งานหรือไม่
    if (!instance) {
      //console.error('🔐 MSAL instance not available');
      return false;
    }
    
    // รับ access token จาก MSAL
    const accounts = instance.getAllAccounts();
    
    if (accounts.length === 0) {
      return false;
    }

    const profileRequestOptions = {
      scopes: ["User.Read"],
      account: accounts[0],
    };

   // console.log('🔐 Acquiring token silently...');
    const tokenTimeoutMs = Number(process.env.REACT_APP_AUTH_TOKEN_TIMEOUT_MS || (process.env.NODE_ENV === 'development' ? 3000 : 7000));
    const tokenPromise = instance.acquireTokenSilent(profileRequestOptions);
    const tokenTimeout = new Promise((_, reject) => 
      setTimeout(() => reject(new Error('Token acquisition timeout')), tokenTimeoutMs)
    );
    let response;
    try {
      response = await Promise.race([tokenPromise, tokenTimeout]);
    } catch (tokenErr) {
      const errMsg = tokenErr?.errorMessage || tokenErr?.message || String(tokenErr);
      const errCode = tokenErr?.errorCode || '';
      // In UAT/first run, user may not have granted Graph consent. Trigger interactive flow.
      const needsInteraction =
        /interaction_required|consent_required|login_required/i.test(errMsg) ||
        /interaction_required|consent_required|login_required/i.test(errCode);
      if (needsInteraction) {
        try {
          await instance.acquireTokenRedirect(profileRequestOptions);
        } catch (redirectErr) {
          // Silent fail
        }
      }
      // Let caller fallback to sessionStorage; return false to avoid loops
      return false;
    }
    //console.log('🔐 Token acquired successfully');
    const accessToken = response.accessToken;

   // console.log('🔐 Calling Microsoft Graph API for user groups...');
    const requiredGroups = JSON.parse(
      process.env.REACT_APP_SECGROUPS_UNIFORM || '[]'
    );

    const graphTimeoutMs = Number(process.env.REACT_APP_AUTH_GRAPH_TIMEOUT_MS || (process.env.NODE_ENV === 'development' ? 3000 : 7000));

    async function checkGroupsWithTimeoutOnce() {
      // POST /me/checkMemberGroups — ต้องการแค่ User.Read ไม่ต้อง admin consent
      const graphPromise = checkMsGraphGroupMembership(accessToken, requiredGroups);
      const graphTimeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Graph API timeout')), graphTimeoutMs)
      );
      return await Promise.race([graphPromise, graphTimeout]);
    }

    // เรียก 1 retry ในกรณี timeout ชั่วคราวของ UAT/Prod
    const graphRetry = String(process.env.REACT_APP_AUTH_GRAPH_RETRY || '0') === '1';
    let matchedGroups = await checkGroupsWithTimeoutOnce();
    if ((!matchedGroups || !Array.isArray(matchedGroups)) && graphRetry) {
      matchedGroups = await checkGroupsWithTimeoutOnce();
    }

    if (!matchedGroups || !Array.isArray(matchedGroups)) {
      return false;
    }

    // ถ้า API คืนกลุ่มที่ตรงกันมา แสดงว่า user อยู่ใน group นั้น
    const hasPermission = matchedGroups.length > 0;

    // เก็บผลลัพธ์ใน cache
    permissionCache.set(cacheKey, {
      hasPermission,
      timestamp: Date.now(),
    });

    // บันทึกกลุ่มที่ตรงกันลง sessionStorage เพื่อใช้ fast-path รอบถัดไป
    try {
      sessionStorage.setItem('usergroup', JSON.stringify(matchedGroups));
    } catch (e) {
      // Silent fail
    }

    return hasPermission;

  } catch (error) {
    // ถ้ามีปัญหากับ token, ลองใช้ข้อมูลจาก sessionStorage เป็น fallback
    // แต่เตือนว่าไม่ปลอดภัย 100%
    
  const groupItems = sessionStorage.getItem('usergroup');
    if (!groupItems) {
      return false;
    }

    try {
      const arrGroup = JSON.parse(groupItems);
      const groups = JSON.parse(
        process.env.REACT_APP_SECGROUPS_UNIFORM || '[]'
      );
      const result = arrGroup.some((item) => groups.includes(item));
      return result;
    } catch (fallbackError) {
      return false;
    }
  }
}